import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getImageUrl, blogSorter } from '../../../utils';
import ContentEditor from '../../ContentEditor';
import NodeActivityEditor from '../../NodeActivityEditor';
import HeaderToolbar from './HeaderToolbar';
import SecondaryButton from '../../SecondaryButton';
import PrimaryButton from '../../PrimaryButton';
import CommentsIcon from '../../../../public/assets/images/theme-2/icons/asterisk-white.svg';
import {
  ViewSection,
  HeaderUserImage,
  HeaderUserName,
  HeaderRequestFeedback,
  AutoSaveStatus,
  PublishWrapper,
  LockInfo,
} from '../styles';
import { EditorHeaderRowLeft, EditorHeaderRowRight, Editor, BlogWrapper } from '../../NodeActivityEditor/styles';
import ImmersiveReaderButton from '../../ImmersiveReaderButton';

const PublicBlogContentViewer = ({
  type,
  report,
  isEditable,
  saveReport,
  fileService,
  integrations,
  autoSaveStatus,
  isTeacher,
  reports,
  user,
}) => {
  const isLocked = report?.locked;

  const [showToolbar, setShowToolbar] = useState(false);
  const [published, setPublished] = useState(false);

  useEffect(() => {
    if (!isTeacher && report?.BlogPosts?.length === 0) {
      saveReport({ ...report, body: ' ' });
    }
  }, []);

  useEffect(() => {
    if (!isTeacher) {
      const isPublished = report.state === 'active' && (report.draft === false || !report.BlogPosts[0]?.draft);
      setPublished(isPublished);
    }
  }, [report]);

  const blogReports = reports
    .sort((a, b) => blogSorter(a, b, user))
    .map((aReport) => {
      const editReport = aReport.id === report?.id;
      const {
        uuid: entityId,
        state: reportState,
        type: entityType,
        body,
        draft,
        Participation,
        BlogPosts,
      } = editReport ? report : aReport;
      const { first_name, last_name, image, id } = Participation.OrganisationParticipation.User;
      const full_name = `${first_name} ${last_name}`;
      const blog = BlogPosts[0] || {};
      const { content, draft: blogDraft, uuid: post_uuid } = blog;
      const reportBody = body || content;
      const reportDraft = draft || blogDraft;

      const workNotStarted = reportState === 'blank' || !reportBody;
      const workInProgress = reportState === 'active' && reportDraft;

      let editorContent = reportBody;
      if (workNotStarted) {
        editorContent = i18n.__('work_not_started');
      } else if (workInProgress) {
        editorContent = i18n.__('work_in_progress');
      }

      const toggleReportComments = () => {
        document.dispatchEvent(new CustomEvent('toggleComments', { detail: { entityId, entityType } }));
      };

      const getUpdatedBlogPost = (draft) => {
        const newBlogPosts = [...BlogPosts];
        if (newBlogPosts.length > 0) {
          newBlogPosts[0].draft = draft;
        }
        return newBlogPosts;
      };

      const onChangeReport = (value) => {
        saveReport({
          ...report,
          body: value,
          state: 'active',
          draft: true,
          post_uuid,
          BlogPosts: getUpdatedBlogPost(true),
        });
      };

      const submitReport = () => {
        saveReport({ ...report, state: 'done', draft: false, post_uuid, BlogPosts: getUpdatedBlogPost(false) });
      };

      const publishReport = () => {
        saveReport({ ...report, state: 'active', draft: false, post_uuid, BlogPosts: getUpdatedBlogPost(false) });
      };

      const revokeReport = () => {
        saveReport({ ...report, state: 'active', draft: true, post_uuid, BlogPosts: getUpdatedBlogPost(true) });
      };

      const headerPanel = (
        <>
          <EditorHeaderRowLeft>
            <HeaderUserImage key={id} src={getImageUrl(fileService, image, '60x60')} title={full_name} />
            <HeaderUserName>{full_name}</HeaderUserName>
          </EditorHeaderRowLeft>
          <EditorHeaderRowRight>
            {editReport ? (
              <HeaderToolbar
                isEditable={isEditable}
                report={report}
                isTeacher={isTeacher}
                showToolbar={showToolbar}
                setShowToolbar={setShowToolbar}
              />
            ) : (
              <>
                <ImmersiveReaderButton />
                <HeaderRequestFeedback onClick={toggleReportComments} src={CommentsIcon} />
              </>
            )}
          </EditorHeaderRowRight>
        </>
      );

      const editorPanel = editReport ? (
        <ContentEditor
          type={type}
          aria-label={i18n.__('noticeboard_texteditor_placeholder')}
          placeholder={i18n.__('noticeboard_texteditor_placeholder')}
          value={reportBody || ''}
          onChange={(value) => isEditable && onChangeReport(value)}
          integrations={integrations}
          fileService={fileService}
          disabled={!isEditable || isTeacher}
          showToolbar={showToolbar}
          className={'ir-content'}
          memoize={true}
        />
      ) : (
        <Editor
          className="content-section ir-content"
          showStatusText={workNotStarted || workInProgress}
          dangerouslySetInnerHTML={{ __html: editorContent }}
        />
      );

      const buttonPanel = editReport && !isTeacher && (
        <>
          <AutoSaveStatus>{autoSaveStatus['contentEditor']}</AutoSaveStatus>
          {isLocked ? (
            <LockInfo>{i18n.__('editor_locked_desc')}</LockInfo>
          ) : isEditable ? (
            <PublishWrapper>
              <PrimaryButton
                text={i18n.__('app_publish_post')}
                onClick={publishReport}
                disabled={isTeacher || published}
                className="publish"
              />
              <SecondaryButton text={i18n.__('app_handin')} onClick={submitReport} />
            </PublishWrapper>
          ) : (
            <PrimaryButton text={i18n.__('app_edit')} onClick={revokeReport} />
          )}
        </>
      );

      return (
        <BlogWrapper key={id}>
          <NodeActivityEditor
            type={type}
            disabled={!editReport}
            headerPanel={headerPanel}
            editorPanel={editorPanel}
            buttonPanel={buttonPanel}
          />
        </BlogWrapper>
      );
    });

  return <ViewSection>{blogReports}</ViewSection>;
};

export default PublicBlogContentViewer;

PublicBlogContentViewer.propTypes = {
  type: PropTypes.string.isRequired,
  report: PropTypes.object,
  reports: PropTypes.array.isRequired,
  isEditable: PropTypes.bool.isRequired,
  saveReport: PropTypes.func.isRequired,
  fileService: PropTypes.object.isRequired,
  integrations: PropTypes.object.isRequired,
  autoSaveStatus: PropTypes.object.isRequired,
  isTeacher: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
};
